import { action, makeAutoObservable, runInAction } from 'mobx';

import CollectionPreviewStorage from '@/collection/services/CollectionPreviewStorage';
import { CollectionPreviewEntry } from '@/collection/stores/CollectionPreviewStore.types';
import useStore from '@/common/hooks/useStore';
import PreviewAPI from '@/media/api/PreviewAPI';
import { PreviewFragment, PreviewType } from '@/media/types/PreviewResponse';

const REQUEST_BATCH_DELAY = 50;
const CACHE_SAVE_DELAY = 500;

class CollectionPreviewStore {
  previewMap = new Map<string, CollectionPreviewEntry>();

  private loadingSet = new Set<string>();

  private queuedSet = new Set<string>();

  private batchTimeout: Nullable<SetTimeout> = null;

  private cacheSaveTimeout: Nullable<SetTimeout> = null;

  constructor(private readonly previewAPI = new PreviewAPI()) {
    makeAutoObservable<CollectionPreviewStore, 'batchTimeout' | 'cacheSaveTimeout' | 'saveToCache' | 'previewAPI'>(
      this,
      {
        batchTimeout: false,
        cacheSaveTimeout: false,
        getPreviewFragments: false,
        isFetching: false,
        ensureFresh: false,
        saveToCache: false,
        previewAPI: false,
      },
    );

    this.restoreFromCache();
  }

  getPreviewFragments = (collectionId: string): Nullable<PreviewFragment[]> => {
    return this.previewMap.get(collectionId)?.fragments ?? null;
  };

  isFetching = (collectionId: string): boolean => {
    return this.loadingSet.has(collectionId) || this.queuedSet.has(collectionId);
  };

  requestPreview = (collectionId: string) => {
    const entry = this.previewMap.get(collectionId);
    const isFresh = !!entry && !entry.isStale;

    if (isFresh || this.queuedSet.has(collectionId) || this.loadingSet.has(collectionId)) {
      return;
    }

    this.queuedSet.add(collectionId);

    if (this.batchTimeout === null) {
      this.batchTimeout = setTimeout(this.flushRequested, REQUEST_BATCH_DELAY);
    }
  };

  private flushRequested = () => {
    this.batchTimeout = null;

    const collectionIds = Array.from(this.queuedSet);

    this.queuedSet.clear();

    if (collectionIds.length === 0) {
      return;
    }

    return this.ensureFresh(collectionIds);
  };

  markAsStale = (collectionIds: string[]) => {
    collectionIds.forEach((id) => {
      const entry = this.previewMap.get(id);

      if (entry) {
        entry.isStale = true;
      }
    });
  };

  ensureFresh = async (collectionIds: string[]) => {
    const missingIds = collectionIds.filter((id) => {
      const entry = this.previewMap.get(id);

      return !entry || entry.isStale;
    });

    if (missingIds.length > 0) {
      return this.fetch(missingIds);
    }
  };

  fetch = async (collectionIds: string[]) => {
    const notAlreadyLoadingIds = collectionIds.filter((id) => !this.loadingSet.has(id));

    if (notAlreadyLoadingIds.length === 0) {
      return;
    }

    this.setLoading(notAlreadyLoadingIds, true);

    try {
      await this.baseFetch(notAlreadyLoadingIds);
    } finally {
      this.setLoading(notAlreadyLoadingIds, false);
    }
  };

  private setLoading = (collectionIds: string[], isLoading: boolean) => {
    collectionIds.forEach((id) => {
      if (isLoading) {
        this.loadingSet.add(id);
      } else {
        this.loadingSet.delete(id);
      }
    });
  };

  private baseFetch = async (collectionIds: string[]) => {
    const collectionPreviews = await this.previewAPI.fetchPreview(
      collectionIds.map((id) => ({ type: PreviewType.Collection, id })),
    );

    runInAction(() => {
      collectionPreviews.forEach((collectionPreview) => {
        this.previewMap.set(collectionPreview.id, { fragments: collectionPreview.preview, isStale: false });
      });
    });

    this.scheduleSaveToCache();
  };

  private restoreFromCache = () => {
    const cached = CollectionPreviewStorage.get();

    Object.entries(cached).forEach(([id, fragments]) => {
      this.previewMap.set(id, { fragments, isStale: true });
    });
  };

  private scheduleSaveToCache = () => {
    if (this.cacheSaveTimeout !== null) {
      return;
    }

    this.cacheSaveTimeout = setTimeout(
      action(() => {
        this.cacheSaveTimeout = null;
        this.saveToCache();
      }),
      CACHE_SAVE_DELAY,
    );
  };

  private saveToCache = () => {
    const state = Object.fromEntries(Array.from(this.previewMap.entries()).map(([id, entry]) => [id, entry.fragments]));

    CollectionPreviewStorage.save(state);
  };
}

export const useCollectionPreviewStore = () => useStore(CollectionPreviewStore);

export const collectionPreviewStore = new CollectionPreviewStore();

export default CollectionPreviewStore;
